import { isRouteErrorResponse, useRouteError } from "react-router";
import { useEffect } from "react";
import axios from 'axios';
import { Logger } from '../../utils/Logger';
import { trackEvent } from "../../services/AppInsightsPlugin";
import { Box, Button, Stack, Typography } from "@mui/material";
import { LogosHeader } from "../../components/LogosHeader";

interface ErrorInfo {
  title: string;
  message: string;
}

/**
 * Détecte le type d'erreur et retourne titre + message appropriés
 */
function getErrorInfo(error: unknown): ErrorInfo {
  // Erreur HTTP via React Router
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: "Page introuvable",
        message: "Cette page n'existe pas ou a été déplacée."
      };
    }
    if (error.status >= 500) {
      return {
        title: "Erreur serveur",
        message: "Une erreur s'est produite. Réessayez dans quelques instants."
      };
    }
    // Autres erreurs HTTP
    return {
      title: "Erreur inattendue",
      message: "Une erreur s'est produite."
    };
  }

  // Erreur Axios (appel API — erreur réseau ou 5xx sans cache SW)
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return {
        title: "Connexion impossible",
        message: "Le serveur est injoignable. Vérifiez votre connexion et réessayez."
      };
    }
    if (error.response.status >= 500) {
      return {
        title: "Erreur serveur",
        message: "Une erreur s'est produite côté serveur. Réessayez dans quelques instants."
      };
    }
  }

  // Erreur réseau générique (non-Axios, ex : WorkboxError 'no-response')
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes("failed to fetch") || msg.includes("network error") || msg.includes("no-response")) {
      return {
        title: "Connexion impossible",
        message: "Le serveur est injoignable. Vérifiez votre connexion et réessayez."
      };
    }
  }

  // Fallback pour erreurs inconnues
  return {
    title: "Erreur inattendue",
    message: "Une erreur s'est produite."
  };
}

export function ErrorPage() {
  const error = useRouteError();

  // Log error to console and Application Insights
  useEffect(() => {
    let errorMessage = "???";
    let errorStatus: number | undefined = undefined;

    if (isRouteErrorResponse(error)) {
      errorMessage = error.statusText || `HTTP ${error.status}`;
      errorStatus = error.status;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    // Log to console
    Logger.error('ErrorPage', `Router error: ${errorMessage}`, error);

    // Track in Application Insights
    trackEvent('ROUTER_ERROR', {
      message: errorMessage,
      status: errorStatus?.toString(),
      type: error instanceof Error ? 'Error' : 'RouteErrorResponse'
    });

    Logger.error('ErrorPage', 'Router error details:', {
      error,
      message: errorMessage,
      status: errorStatus
    });
  }, [error]);

  const errorInfo = getErrorInfo(error);

  const handleReturnHome = () => window.location.reload();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        py: 2,
        px: 1
      }}
    >
      {/* Logos section */}
      <LogosHeader sx={{ mb: 4 }} />

      {/* Message section */}
      <Stack spacing={2} sx={{ width: '100%', maxWidth: '400px', mt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {errorInfo.title}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {errorInfo.message}
        </Typography>

        {/* Return home button */}
        <Button variant="contained" onClick={handleReturnHome} sx={{ mt: 2 }}>
          Réessayer
        </Button>
      </Stack>
    </Box>
  );
}
